import { useEffect, useRef, useState } from 'react';

import { Button } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { Modal, ModalProps, RadioChangeEvent, Typography } from 'antd';
import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useDeleteGroupSpecificLabel } from '~hooks/queries/custom-labels/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import { StepFooter } from './common/component/RemoveModal/Footer';
import RemoveTypeSelectStepContnet from './common/component/RemoveModal/RemoveTypeSelectStepContent';
import GroupMigrationList from './GroupMigrationList';
import GroupTreeSelect from './GroupTreeSelect';
import useGroupLabelTreeStore from './store/groupLabelTreeStore';

interface Props extends ModalProps {
  item: FlattenTreeResult<GroupTreeItem>;
  defaultStep?: number;
  setModalOpened?: (value: boolean) => void;
}

const GroupLabelRemoveModal: React.FC<Props> = ({
  item,
  defaultStep,
  setModalOpened,
  ...props
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const groupLabelTree = useGroupLabelTreeStore().groupLabelTree;

  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [changeParentCategory, setChangeParentCategory] =
    useState<FlattenTreeResult<GroupTreeItem> | null>(null);
  const [removalType, setRemovalType] = useState('remove');
  const [step, setStep] = useState(defaultStep ?? 1);

  const [changeMap, setChangeMap] = useState<Record<string, string>>({});
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const { mutateAsync: deleteCategories, isLoading } = useDeleteGroupSpecificLabel();

  const flattenedTree = flattenTree(groupLabelTree);
  const onChange = (e: RadioChangeEvent) => {
    setRemovalType(e.target.value);
    if (e.target.value === 'remove' && changeParentCategory) {
      setChangeParentCategory(null);
    }
  };

  const label = '항목';

  const onRemove = async () => {
    // changeMap에 있는 자식들은 migration
    // 본인은 삭제 or migration

    if (!item?.value?.id || !item?.value?.id) return;

    const response = await deleteCategories({
      companyId,
      id: item.value.id,

      request: {
        moveChildrenTo: changeParentCategory?.key ?? null,
        migrations: Object.entries(changeMap).map(([from, to]) => {
          return {
            from: Number(from),
            to: to ? Number(to) : null,
          };
        }),
      },
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('삭제되었습니다.');
      mixpanel.track(MIXPANEL_EVENT.GROUP.DELETE_DETAILED_GROUP, {
        type: changeParentCategory?.key ? 'CHANGE_PARENT' : 'DIRECT_DELETE',
      });
      mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: 'GROUP_DELETE',
      });
      //@ts-ignore
      props.onCancel();
    } else {
      Toast.error('삭제에 실패했습니다.');
      //@ts-ignore
      props.onCancel();
    }
  };

  const getStepContent = () => {
    const childrenTree = flattenTree(item.children).filter((item) => item.children.length > 0);

    switch (step) {
      case 1:
        return (
          <RemoveTypeSelectStepContnet
            label={label}
            item={item}
            childrenTree={childrenTree}
            onChange={onChange}
            removalType={removalType}
            treeSelectSection={
              <GroupTreeSelect
                onDropdownOpenChange={setDropdownOpened}
                disableActionOnInactiveItem={false}
                closeOnSelect
                treeRef={wrapperRef}
                footerElement={<div />}
                onSelect={(_, key) => {
                  const target = flattenedTree.find((node) => key.includes(node.key.toString()));
                  if (!target) return;
                  setChangeParentCategory(target);
                }}
                triggerElement={
                  <Button
                    border
                    disable={removalType !== 'change-parent'}
                    style={{
                      width: 'fit-content',
                      marginLeft: 18,
                    }}
                  >
                    <Typography.Text
                      style={{
                        color:
                          removalType !== 'change-parent' ? Colors.grayOpacity22 : Colors.gray700,
                        ...typographyStyle['14_400'],
                      }}
                    >
                      {changeParentCategory
                        ? flattenedTree.find(
                            (item) => item.value?.id === changeParentCategory?.value?.id,
                          )?.value?.name
                        : '항목 선택'}
                    </Typography.Text>
                  </Button>
                }
                treeList={flattenedTree.filter((treeItem) => {
                  return (
                    !childrenTree.map((c) => c.key).includes(treeItem.key) &&
                    treeItem.key !== item.key &&
                    !!treeItem.value?.name
                  );
                })}
              />
            }
          />
        );
      case 2:
        const depth = item.namePath.split('.').length;

        const filteredChildrenTree = [item, ...childrenTree];
        const targetItems =
          removalType === 'change-parent' || defaultStep === 2 ? [item] : filteredChildrenTree;

        return (
          <GroupMigrationList
            changeMap={changeMap}
            setChangeMap={setChangeMap}
            targetItems={targetItems}
            depth={depth}
            parentItem={item}
          />
        );
    }
  };

  useEffect(() => {
    setModalOpened?.(true);

    return () => {
      setModalOpened?.(false);
    };
  }, []);

  return (
    <Modal
      keyboard={!dropdownOpened}
      title={
        <Typography.Text
          style={{
            ...typographyStyle['16_600'],
            color: Colors.gray800,
          }}
        >
          {`${item.value?.name} ${label} 삭제`}
        </Typography.Text>
      }
      footer={
        <StepFooter
          step={step}
          setChangeMap={setChangeMap}
          setStep={setStep}
          onRemove={onRemove}
          // @ts-ignore
          onCancel={() => props.onCancel()}
          isLoading={isLoading}
        />
      }
      {...props}
    >
      {getStepContent()}
    </Modal>
  );
};

export default GroupLabelRemoveModal;
